import { useMemo, useState } from 'react'
import { useQuery, useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { BanknoteIcon, PaperclipIcon, PlusIcon, XIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import { Input } from '@ems/ui/components/input'
import { Combobox } from '@ems/ui/patterns/combobox'
import { SearchInput } from '@ems/ui/patterns/search-input'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'
import { formatDateTime, formatMoney } from '@ems/ui/lib/format'

import { OptionSelect } from '#admin/components/option-select.tsx'
import { pageSlice, TablePagination, usePagination } from '#admin/components/table-pagination.tsx'
import { ExportLink } from '#admin/features/billing/export-link.tsx'
import { useLiveSearch } from '#admin/lib/use-live-search.ts'
import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { label } from '#admin/features/billing/api.ts'
import { PaymentsSkeleton } from '#admin/features/billing/billing-skeletons.tsx'
import { PayerName } from '#admin/features/billing/payer-name.tsx'
import {
  methodLabel,
  PAYMENT_METHODS,
  PAYMENT_STATUSES,
  paymentTone,
  unallocated,
  type Payment,
} from '#admin/features/billing/payments-api.ts'
import { companiesQuery, paymentsQuery } from '#admin/features/billing/queries.ts'
import { RecordPaymentDialog } from '#admin/features/billing/record-payment-dialog.tsx'
import {
  parsePaymentSearch,
  paymentFiltersOf,
  type PaymentSearch,
} from '#admin/features/billing/search.ts'
import { manifestQuery } from '#admin/features/tenant/queries.ts'

/**
 * `/billing/payments` — money received, and how much of each payment is still unallocated.
 * Pending payments are the operator's queue: confirm them when the statement shows them.
 */
export const Route = createFileRoute('/_authenticated/billing/payments/')({
  pendingComponent: PaymentsSkeleton,
  validateSearch: parsePaymentSearch,
  loaderDeps: ({ search }) => paymentFiltersOf(search),
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureInfiniteQueryData(paymentsQuery(deps)),
      context.queryClient.ensureQueryData(manifestQuery()),
      // Only for the organization filter. The page works without it, so a failure does not stop it.
      context.queryClient.prefetchQuery(companiesQuery()),
    ]),
  component: PaymentsPage,
})

const ANY = 'all'

function PaymentsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery())
  const { data: manifest } = useSuspenseQuery(manifestQuery())
  const canManage = can(session, 'billing.manage')
  const search = Route.useSearch()
  const filters = paymentFiltersOf(search)
  const navigate = useNavigate({ from: Route.fullPath })
  const [draft, setDraft] = useLiveSearch(
    filters.q,
    (term) =>
      void navigate({ search: (prev) => parsePaymentSearch({ ...prev, q: term }), replace: true }),
  )
  const [recording, setRecording] = useState(false)
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useSuspenseInfiniteQuery(
    paymentsQuery(filters),
  )
  const { data: companies } = useQuery(companiesQuery())
  const payments = data.pages.flatMap((page) => page.items)
  const total = data.pages[0]?.total ?? 0
  // One page at a time, 10 rows unless the operator chooses more. Any filter, search or new page
  // size starts again at the first page.
  const { page, setPage, pageSize, setPageSize } = usePagination(JSON.stringify(filters))
  const paged = pageSlice(payments, page, pageSize)
  const filtered = Object.values(filters).some((value) => value !== '')

  const setFilter = (key: keyof PaymentSearch, value: string) =>
    void navigate({ search: (prev) => parsePaymentSearch({ ...prev, [key]: value }) })

  const columns = useMemo<ColumnDef<Payment, never>[]>(
    () => [
      {
        accessorKey: 'reference',
        header: 'Reference',
        meta: { primary: true },
        cell: ({ row }) => (
          <Link
            to="/billing/payments/$paymentId"
            params={{ paymentId: row.original.id }}
            className="font-mono text-xs after:absolute after:inset-0 hover:underline"
          >
            {row.original.reference}
          </Link>
        ),
      },
      {
        id: 'payer',
        header: 'Payer',
        meta: { truncate: true },
        cell: ({ row }) => <PayerName payment={row.original} />,
      },
      { id: 'method', header: 'Method', cell: ({ row }) => methodLabel(row.original.method) },
      {
        id: 'proof',
        header: 'Proof',
        meta: { hideOnCard: true },
        cell: ({ row }) =>
          row.original.proofKey === null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className="inline-flex items-center gap-1">
              <PaperclipIcon className="size-4" aria-hidden />
              Attached
            </span>
          ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusBadge tone={paymentTone(row.original.status)}>
            {label(row.original.status)}
          </StatusBadge>
        ),
      },
      {
        id: 'paidAt',
        header: 'Received',
        meta: { hideOnCard: true },
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {row.original.paidAt === null ? '—' : formatDateTime(row.original.paidAt)}
          </span>
        ),
      },
      {
        id: 'amount',
        header: 'Amount',
        meta: { align: 'end' },
        cell: ({ row }) => formatMoney(row.original.amount, row.original.currency),
      },
      {
        id: 'unallocated',
        header: 'Unallocated',
        meta: { align: 'end' },
        cell: ({ row }) => {
          const left = unallocated(row.original)
          return row.original.status !== 'completed' || left === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className="text-warning font-medium">
              {formatMoney(left, row.original.currency)}
            </span>
          )
        },
      },
    ],
    [],
  )

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Payments"
        description="Money received against invoices — by virtual account, transfer, card or at the desk."
        actions={
          <>
            <ExportLink
              path="/v1/billing/admin/payments/export"
              filters={filters}
              filtered={filtered}
            />
            {canManage ? (
              <Button onClick={() => setRecording(true)}>
                <PlusIcon />
                Record payment
              </Button>
            ) : null}
          </>
        }
      />

      {canManage ? null : <ReadOnlyNotice area="payments" />}

      <div className="flex flex-wrap items-end gap-2">
        <div role="search" className="flex w-full min-w-0 sm:w-auto sm:min-w-64 sm:flex-1">
          <SearchInput
            aria-label="Search by reference, payer or virtual account"
            placeholder="Search reference, payer or virtual account"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
        </div>
        <OptionSelect
          aria-label="Status"
          className="w-full sm:w-44"
          value={filters.status === '' ? ANY : filters.status}
          onValueChange={(value) => setFilter('status', value)}
          options={[
            { value: ANY, label: 'Any status' },
            ...PAYMENT_STATUSES.map((value) => ({ value, label: label(value) })),
          ]}
        />
        <OptionSelect
          aria-label="Method"
          className="w-full sm:w-44"
          value={filters.method === '' ? ANY : filters.method}
          onValueChange={(value) => setFilter('method', value)}
          options={[
            { value: ANY, label: 'Any method' },
            ...PAYMENT_METHODS.map((value) => ({ value, label: methodLabel(value) })),
          ]}
        />
        <Combobox
          aria-label="Organization"
          className="w-full sm:w-56"
          value={filters.companyId === '' ? ANY : filters.companyId}
          onValueChange={(value) => setFilter('companyId', value)}
          placeholder="Any organization"
          searchPlaceholder="Search organizations…"
          options={[
            { value: ANY, label: 'Any organization' },
            ...(companies?.items ?? []).map((company) => ({
              value: company.id,
              label: company.name,
            })),
          ]}
        />
        <Input
          type="date"
          aria-label="Paid from"
          title="Paid from"
          className="min-w-0 flex-1 sm:w-40 sm:flex-none"
          value={filters.paidFrom}
          max={filters.paidTo === '' ? undefined : filters.paidTo}
          onChange={(event) => setFilter('paidFrom', event.target.value)}
        />
        <Input
          type="date"
          aria-label="Paid to"
          title="Paid to"
          className="min-w-0 flex-1 sm:w-40 sm:flex-none"
          value={filters.paidTo}
          min={filters.paidFrom === '' ? undefined : filters.paidFrom}
          onChange={(event) => setFilter('paidTo', event.target.value)}
        />
        {/* Beside the filters it clears, and only while one is on. */}
        {filtered ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setDraft('')
              void navigate({ search: {} })
            }}
            className="text-muted-foreground hover:text-foreground h-9 gap-1.5"
          >
            <XIcon className="size-4" />
            Clear filters
          </Button>
        ) : null}
      </div>

      <DataTable
        caption="Payments"
        title={`${filtered ? 'Matching payments' : 'All payments'} (${total})`}
        // The total is the server's count of every match. The rows come a batch at a time, so
        // Next asks for the next batch when its page goes past the rows that are here. Until they
        // arrive, the table stays on the last page it can show. Only with rows: an empty table
        // shows its empty state, and no line under it.
        footer={
          total === 0 ? undefined : (
            <TablePagination
              page={paged.page}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              totalItems={total}
              onPageChange={(nextPage) => {
                setPage(nextPage)
                // Not while a fetch is on its way: a second click would ask for the same rows.
                if (
                  (nextPage + 1) * pageSize > payments.length &&
                  hasNextPage &&
                  !isFetchingNextPage
                ) {
                  void fetchNextPage()
                }
              }}
              ariaLabel="Pages of payments"
            />
          )
        }
        columns={columns}
        data={paged.rows}
        getRowId={(payment) => payment.id}
        empty={
          <EmptyState
            icon={<BanknoteIcon />}
            title={filtered ? 'Nothing matches' : 'No payments yet'}
            description={
              filtered
                ? 'No payment matches all the filters. The search looks at our reference, the provider’s, the payer and the virtual account.'
                : 'Payments arrive from virtual accounts automatically, or are recorded here by hand.'
            }
          />
        }
      />

      {recording ? (
        <RecordPaymentDialog
          invoice={null}
          currency={manifest?.tenant.currencyCode ?? 'IDR'}
          zone={manifest?.tenant.timezone ?? 'UTC'}
          canUpload={can(session, 'identity.write')}
          onClose={() => setRecording(false)}
        />
      ) : null}
    </div>
  )
}
